<script setup lang="ts">
import { useAuthStore } from "~/store/auth/auth.store";
import { AuthModeEnum } from "~/utils/enums/auth.enums";

withDefaults(
  defineProps<{
    showAccount: boolean;
    /**
     * Gap between the bottom of the trigger and the panel. The home header positions the panel at
     * the avatar's own bottom, so 10px is the whole offset there; the app header anchors it inside
     * the avatar, which floats above the bottom of the bar, and asks for enough to clear it.
     */
    topOffset?: string;
  }>(),
  {
    topOffset: "10px",
  },
);

const emit = defineEmits(["close"]);

const authStore = useAuthStore();

function navigateToLogAs() {
  emit("close");
  if (authStore.mode === AuthModeEnum.IMPERSONATION) return authStore.exitImpersonation();
  return navigateTo(`/admin/log-as`);
}
</script>

<template>
  <div class="dropdown_account" :class="[showAccount ? 'flex flex-col' : 'hidden']" :style="{ marginTop: topOffset }">
    <button class="disconnect_button text_med_14" @click="authStore.logOut()">Se déconnecter</button>
    <button v-if="authStore.isSimbelAdmin" class="text_med_14 disconnect_button" @click="navigateToLogAs">
      Log as
    </button>
  </div>
</template>

<style scoped lang="scss">
/*
 * Hangs off the avatar it belongs to — `top-full` rather than the static position, which moved with
 * the trigger into a flex row and started centring the dropdown on the header bar. The home header
 * teleports this to `body` and overrides `top`/`left` inline, so it is unaffected.
 *
 * z-700 puts it over the assistant panel (600): it is opened from the header, so it has to stay
 * reachable whatever is already floating. Reference points: GlobalModal 500, CenterModal 501.
 *
 * The 2px overhang past the avatar is deliberate: it keeps the panel exactly where it sat before
 * the header was split, when it hung off the full-width container at 30px from its edge rather
 * than off the avatar at 32px. Ignored on the home header, which positions with an inline `left`.
 */
.dropdown_account {
  @apply absolute justify-center right-[-2px] top-full rounded-[6px] p-[12px] z-[700] w-[180px];
  background: var(--white-main);
  box-shadow: var(--shadow-hard);
}

.dropdown_account button {
  @apply text-center;
  color: var(--text-tertiary-color);

  &:hover {
    color: var(--primary-blue-color);
  }
}
</style>
